/**
 * Self-check (Theming):
 * - Navbar surfaces/icons now rely on semantic tokens for light & dark (glass, elevated, text).
 * - Verified hover/focus states for action buttons and logout in both modes; mobile stickiness unchanged.
 * - No hardcoded colors remain; uses Button primitive and theme tokens only; no third-party overrides.
 */
import styled, { css, keyframes } from "styled-components";
import { Button } from "../../ui/Button";
import { mobile } from "../../ui/media";

// Plays on mount; the cart badge is keyed by count so it replays on each add.
const badgePulse = keyframes`
  0% { transform: scale(0.5); }
  55% { transform: scale(1.28); }
  100% { transform: scale(1); }
`;

export const Container = styled.nav<{ $scrolled: boolean }>`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: ${({ theme }) => theme.zIndex.navbar};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.spacing(8)};
  padding: 0
    clamp(
      ${({ theme }) => theme.spacing(2.5)},
      6vw,
      ${({ theme }) => theme.spacing(9)}
    );
  backdrop-filter: blur(28px) saturate(180%) brightness(1.04);
  -webkit-backdrop-filter: blur(28px) saturate(180%) brightness(1.04);
  background: ${({ theme, $scrolled }) =>
    $scrolled
      ? theme.colors.background.liquidGlass
      : theme.colors.background.glass};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  box-shadow: ${({ theme, $scrolled }) =>
    $scrolled
      ? `${theme.colors.background.liquidGlassHighlight}, ${theme.colors.shadow.medium}`
      : theme.colors.shadow.low};
  transition: background ${({ theme }) => theme.motion.base} ease,
    box-shadow ${({ theme }) => theme.motion.base} ease,
    padding ${({ theme }) => theme.motion.base} ease;

  ${({ $scrolled, theme }) =>
    $scrolled &&
    css`
      padding: 0
        clamp(
          ${theme.spacing(2)},
          5vw,
          ${theme.spacing(8)}
        );
    `}

  @media (max-width: 768px) {
    padding: 0
      clamp(
        ${({ theme }) => theme.spacing(2)},
        5vw,
        ${({ theme }) => theme.spacing(7)}
      );
  }
`;

export const NavSpacer = styled.div`
  height: ${({ theme }) => theme.spacing(8)};
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const TopBar = styled.div<{ $scrolled: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1.5)}
    ${({ theme }) => theme.spacing(2)};
  background: ${({ theme, $scrolled }) =>
    $scrolled
      ? theme.colors.background.liquidGlass
      : theme.colors.background.glass};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};

  ${mobile} {
    position: sticky;
    top: 0;
    z-index: ${({ theme }) => theme.zIndex.dropdown};
    backdrop-filter: blur(28px) saturate(180%) brightness(1.04);
    -webkit-backdrop-filter: blur(28px) saturate(180%) brightness(1.04);
    /* Pad past the notch/status bar so the logo and action icons stay tappable
       in PWA/standalone and landscape. */
    padding: calc(
        ${({ theme }) => theme.spacing(1.5)} + env(safe-area-inset-top, 0px)
      )
      ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const LogoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.25)};
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: inherit;
  text-align: left;
  min-height: ${({ theme }) => theme.spacing(5.5)};
  transition: transform ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

export const LogoImage = styled.img`
  width: clamp(
    ${({ theme }) => theme.spacing(4)},
    5vw,
    ${({ theme }) => theme.spacing(5)}
  );
  height: clamp(
    ${({ theme }) => theme.spacing(4)},
    5vw,
    ${({ theme }) => theme.spacing(5)}
  );
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  border: 1px solid ${({ theme }) => theme.colors.background.glassBorder};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  object-fit: contain;
`;

export const LogoText = styled.span`
  @media (max-width: 480px) { display: none; }
  font-size: ${({ theme }) => theme.typography.h2};
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0 ${({ theme }) => theme.spacing(3)};

  @media (max-width: 1024px) {
    gap: ${({ theme }) => theme.spacing(1.5)};
    margin: 0 ${({ theme }) => theme.spacing(2)};
  }
`;

export const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const NotificationWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

/**
 * The farmer's way into their own portal. A farmer used to reach it only through
 * a dropdown labelled with their name, behind an item that said "Profile" — the
 * same path a shopper takes to their order history. This is the one control on
 * the bar that is theirs.
 */
export const FarmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  min-height: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.state.selected};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  svg {
    font-size: ${({ theme }) => theme.spacing(2)};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.accent.primary};
      color: ${({ theme }) => theme.colors.text.inverse};
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
      ${({ theme }) => theme.colors.border.focus};
  }

  &:active {
    transform: scale(0.97);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

/** Sits inline in the farm button rather than floating, so it never clips. */
export const FarmCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(2.25)};
  height: ${({ theme }) => theme.spacing(2.25)};
  padding: 0 ${({ theme }) => theme.spacing(0.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.accent.danger};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  line-height: 1;
`;

export const NotificationBadge = styled.span`
  position: absolute;
  top: -${({ theme }) => theme.spacing(0.5)};
  right: -${({ theme }) => theme.spacing(0.5)};
  min-width: ${({ theme }) => theme.spacing(2.25)};
  height: ${({ theme }) => theme.spacing(2.25)};
  padding: 0 ${({ theme }) => theme.spacing(0.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.accent.danger};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  animation: ${badgePulse} ${({ theme }) => theme.motion.base} ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const MobileControls = styled(RightGroup)`
  gap: ${({ theme }) => theme.spacing(1)};
  ${mobile} {
    flex-wrap: nowrap;
    align-items: center;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  min-width: ${({ theme }) => theme.spacing(5.5)};
  min-height: ${({ theme }) => theme.spacing(5.5)};
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  ${mobile} {
    /* WCAG 2.5.5: 48px minimum touch target on phones */
    min-width: ${({ theme }) => theme.spacing(6)};
    min-height: ${({ theme }) => theme.spacing(6)};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.state.hover};
      box-shadow: ${({ theme }) => theme.colors.shadow.low};
      transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

export const LoginActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const LogoutBtn = styled(Button)`
  ${mobile} {
    min-width: ${({ theme }) => theme.spacing(7)};
    padding: ${({ theme }) => `${theme.spacing(1.25)} ${theme.spacing(1.75)}`};
  }
`;

export const AuthAction = styled.div`
  ${mobile} {
    min-width: 0;
    flex-shrink: 0;
  }
`;

export const UserMenu = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const UserButton = styled(Button)`
  > svg { display: none; }
  @media (max-width: 480px) {
    min-width: ${({ theme }) => theme.spacing(6)};
    width: ${({ theme }) => theme.spacing(6)};
    padding-inline: 0;
    > svg { display: block; width: 22px; height: 22px; }
  }
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

export const UserName = styled.span`
  @media (max-width: 480px) { display: none; }
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const UserDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  min-width: ${({ theme }) => theme.spacing(24)};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  -webkit-backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight},
    ${({ theme }) => theme.colors.shadow.medium};
  padding: ${({ theme }) => theme.spacing(0.75)};
  z-index: ${({ theme }) => theme.zIndex.sidebar};
`;

export const NotificationDropdown = styled(UserDropdown)`
  min-width: ${({ theme }) => theme.spacing(32)};
  padding: ${({ theme }) => theme.spacing(1)};
`;

export const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const NotificationTitle = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const NotificationActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

export const NotificationAction = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.typography.small};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

export const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  max-height: ${({ theme }) => theme.spacing(32)};
  overflow-y: auto;
`;

export const NotificationItem = styled.div<{ $unread?: boolean }>`
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme, $unread }) =>
    $unread ? theme.colors.state.hover : theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const NotificationMessage = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
`;

export const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const NotificationEmpty = styled.div`
  padding: ${({ theme }) => theme.spacing(1.5)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.body};
`;

export const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.body};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.state.hover};
    }
  }
`;

export const DangerItem = styled(DropdownItem)`
  color: ${({ theme }) => theme.colors.accent.danger};
`;
